import { FC, useEffect, useState } from 'react'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import cn from './index.module.css'
import Modal from '@common/Modal'
import classNames from 'classnames'
import { Modal as AntModal } from 'antd'
import { IcoRemove } from '@/utils/icons'
import TeacherSalaryFields from './TeacherSalaryFields'
import SalaryRecalculationFields from './SalaryRecalculationFields'
import { usePlatformTimeZone } from '@/hooks/usePlatformTimeZone'
import PrimaryButton from '@pages/Admin/CourseSettings/components/PrimaryButton/PrimaryButton'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import { ITeacherSalary, TeacherSalaryType } from '../../types'
import { useAddAlert } from '@/hooks/useAddAlert'
import UserCard from '@pages/Admin/Manage/tabs/ClassesPage/components/ShowUsersModal/components/UserCard/UserCard'
import {
  updateTeacherSalary,
  updateTeacherSalaryForAllGroups,
} from './utils/api'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import { useGetGroupName } from '@pages/Admin/PlatformSettings2/AddSettingsEmployeesPersonAccount/hooks/useGetGroupName'
import {
  isThresholdSalary,
  isValidPaidCancelSalaryPercent,
  isValidTeacherStudentRates,
} from '../../utils/teacherSalary'
import jclasses from '@pages/Admin/CRM/styles/jsmodal.module.css'

interface EditTeacherSalaryModalProps {
  teacher?: IUser
  group?: IGroup
  teacherSalary?: ITeacherSalary | null
  duration?: number | null
  mode?: 'group' | 'all-groups'
  hasLessonPaymentGroups?: boolean
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const DEFAULT_SALARY_TYPE: TeacherSalaryType = 'per-lesson'
const EditTeacherSalaryModalContent: FC<EditTeacherSalaryModalProps> = ({
  teacher,
  group,
  teacherSalary,
  duration,
  mode = 'group',
  hasLessonPaymentGroups = false,
  open,
  onClose,
  onSuccess,
}) => {
  const platformTimeZone = usePlatformTimeZone()
  const { groupNameSingular } = useGetGroupName()
  const addAlert = useAddAlert()
  const isAllGroupsMode = mode === 'all-groups'
  const showPaidCancelPercent = isAllGroupsMode ? hasLessonPaymentGroups : group?.type === 'lesson-payment' || (group as any)?.group_type === 'lesson-payment'
  const [submitting, setSubmitting] = useState(false)
  const [salaryType, setSalaryType] =
    useState<TeacherSalaryType>(DEFAULT_SALARY_TYPE)
  const [salaryValue, setSalaryValue] = useState('')
  const [durationMinutes, setDurationMinutes] = useState('')
  const [minAmountEnabled, setMinAmountEnabled] = useState(false)
  const [minAmount, setMinAmount] = useState('')
  const [paidCancelPercent, setPaidCancelPercent] = useState('0')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [applyToCompleted, setApplyToCompleted] = useState(false)
  const [studentRates, setStudentRates] = useState<Array<{ count: string; salary: string }>>([])

  useEffect(() => {
    if (!open) return

    setEffectiveFrom('')
    setApplyToCompleted(false)
    setStudentRates(Object.entries(teacherSalary?.student_rates ?? {}).map(([count, salary]) => ({ count, salary: String(salary) })))
    setSalaryType(teacherSalary?.type ?? DEFAULT_SALARY_TYPE)
    setPaidCancelPercent(String(teacherSalary?.paid_cancel_percent ?? 0))
    setSalaryValue(
      teacherSalary?.salary !== null && teacherSalary?.salary !== undefined
        ? String(teacherSalary.salary)
        : '',
    )
    setDurationMinutes(
      duration !== null && duration !== undefined
        ? String(duration)
        : teacherSalary?.duration_minutes !== null &&
            teacherSalary?.duration_minutes !== undefined
          ? String(teacherSalary.duration_minutes)
          : '',
    )
    setMinAmountEnabled(!!teacherSalary?.min_amount_enabled)
    setMinAmount(
      teacherSalary?.min_amount !== null && teacherSalary?.min_amount !== undefined
        ? String(teacherSalary.min_amount)
        : '',
    )
  }, [duration, teacherSalary, open, platformTimeZone])

  const showDurationField = salaryType === 'per-duration'
  const showMinAmountFields = salaryType === 'per-student-hour'

  const salaryChanged = salaryType !== (teacherSalary?.type ?? DEFAULT_SALARY_TYPE)
          || salaryValue !== String(teacherSalary?.salary ?? '')
          || durationMinutes !== String(duration ?? teacherSalary?.duration_minutes ?? '')
          || minAmountEnabled !== !!teacherSalary?.min_amount_enabled || minAmount !== String(teacherSalary?.min_amount ?? '')
          || paidCancelPercent !== String(teacherSalary?.paid_cancel_percent ?? 0)
          || JSON.stringify(studentRates) !== JSON.stringify(Object.entries(teacherSalary?.student_rates ?? {}).map(([count, salary]) => ({ count, salary: String(salary) })))

  const onSubmit = async () => {
    if (!teacher?.id || (!isAllGroupsMode && !group?.id)) {
      addAlert({
        title: 'Ошибка',
        text: isAllGroupsMode
          ? 'Не удалось определить педагога'
          : 'Не удалось определить группу или педагога',
        type: 'error',
      })
      return
    }

    if (!isThresholdSalary(salaryType) && (!salaryValue || !Number.isFinite(Number(salaryValue)) || Number(salaryValue) < 0)) {
      addAlert({
        title: 'Ошибка',
        text: 'Укажите зарплату',
        type: 'error',
      })
      return
    }

    if (showDurationField && !durationMinutes) {
      addAlert({
        title: 'Ошибка',
        text: 'Укажите длительность занятия',
        type: 'error',
      })
      return
    }

    if (showMinAmountFields && minAmountEnabled && minAmount === '') {
      addAlert({
        title: 'Ошибка',
        text: 'Укажите минимальную сумму',
        type: 'error',
      })
      return
    }

    if (showPaidCancelPercent && !isValidPaidCancelSalaryPercent(paidCancelPercent)) {
      addAlert({ title: 'Укажите процент ЗП при отмене от 0 до 100', type: 'error' })
      return
    }
    if (applyToCompleted && !effectiveFrom) {
      addAlert({ title: 'Укажите дату начала действия ставки', type: 'error' })
      return
    }
    if ((salaryType === 'per-lesson' || isThresholdSalary(salaryType)) && !isValidTeacherStudentRates(studentRates, isThresholdSalary(salaryType))) {
      addAlert({ title: 'Укажите уникальное целое число учеников и неотрицательную сумму для каждой ставки', type: 'error' })
      return
    }
    setSubmitting(true)

    try {
      const payload = {
        teacher_id: Number(teacher.id),
        ...(applyToCompleted && salaryChanged ? { effective_from: effectiveFrom, apply_to_completed: true } : {}),
        student_rates: salaryType === 'per-lesson' || isThresholdSalary(salaryType) ? Object.fromEntries(studentRates.map((rate) => [String(Number(rate.count)), Number(rate.salary)])) : {},
        type: salaryType,
        salary: isThresholdSalary(salaryType) ? 0 : Number(salaryValue),
        duration_minutes:
          salaryType === 'per-student-hour'
            ? 60
            : showDurationField
              ? Number(durationMinutes)
              : null,
        min_amount_enabled: showMinAmountFields ? minAmountEnabled : false,
        min_amount:
          showMinAmountFields && minAmountEnabled ? Number(minAmount) : null,
        ...(showPaidCancelPercent ? { paid_cancel_percent: Number(paidCancelPercent) } : {}),
      }

      const response = isAllGroupsMode
        ? await updateTeacherSalaryForAllGroups(payload)
        : await updateTeacherSalary({
            group_id: Number(group?.id),
            ...payload,
          })

      if (!response?.success) {
        throw new Error()
      }

      onSuccess?.()
      addAlert({
        title: 'Успешно',
        text: isAllGroupsMode
          ? 'Зарплата педагога обновлена для всех групп'
          : 'Зарплата педагога сохранена',
        type: 'success',
      })
      onClose()
    } catch (error) {
      addAlert({
        title: 'Ошибка',
        text: isAllGroupsMode
          ? 'Не удалось сохранить зарплату для всех групп педагога'
          : 'Не удалось сохранить зарплату педагога',
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const onConfirmSubmit = () => {
    if (!isAllGroupsMode) {
      onSubmit()
      return
    }

    AntModal.confirm({
      title: 'Обновить зарплату для всех групп',
      content:
        'Вы уверены, что хотите обновить зарплату педагога сразу для всех его групп?',
      icon: null,
      centered: true,
      maskClosable: true,
      className: jclasses.modal,
      okText: 'Обновить',
      cancelText: 'Отменить',
      onOk: () => onSubmit(),
    })
  }

  return (
    <div className={classNames(mclasses.modalContainer, cn.modalContainer)}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>
            Изменить зарплату педагога
          </span>
        </div>
        <div className={mclasses.closeIcon} onClick={() => onClose()}>
          <IcoRemove />
        </div>
      </div>
      <div className={classNames(mclasses.formWrapper, cn.form)}>
        <div className={cn.header}>
          {teacher && <UserCard user={teacher} className={cn.headerBlock} />}
          <div className={classNames(cn.headerBlock, cn.groupInfo)}>
            <FieldWrapper label={groupNameSingular}>
              <span className={cn.groupName}>
                {isAllGroupsMode ? 'Для всех' : group?.name ?? '-'}
              </span>
            </FieldWrapper>
          </div>
        </div>
        <TeacherSalaryFields lessonPayment={showPaidCancelPercent && (!isAllGroupsMode || hasLessonPaymentGroups)}
          value={{ salaryType, salary: salaryValue, salaryDurationMinutes: durationMinutes, minAmountEnabled, minAmount, paidCancelPercent, studentRates }}
          onChange={(update) => {
            if (update.salaryType !== undefined) setSalaryType(update.salaryType)
            if (update.salary !== undefined) setSalaryValue(update.salary)
            if (update.salaryDurationMinutes !== undefined) setDurationMinutes(update.salaryDurationMinutes)
            if (update.minAmountEnabled !== undefined) setMinAmountEnabled(update.minAmountEnabled)
            if (update.minAmount !== undefined) setMinAmount(update.minAmount)
            if (update.paidCancelPercent !== undefined) setPaidCancelPercent(update.paidCancelPercent)
            if (update.studentRates !== undefined) setStudentRates(update.studentRates)
          }} />
        <SalaryRecalculationFields changed={salaryChanged}
          applyToCompleted={applyToCompleted} onApplyChange={setApplyToCompleted} effectiveFrom={effectiveFrom} onDateChange={setEffectiveFrom}
          scope={{ group_id: isAllGroupsMode ? undefined : group?.id, teacher_id: teacher?.id }} />
      </div>
      <div className={mclasses.actions}>
        <SecondaryButton onClick={onClose}>
          Отменить
        </SecondaryButton>
        <PrimaryButton
          onClick={onConfirmSubmit}
          isLoading={submitting}
          disabled={submitting}
        >
          Сохранить
        </PrimaryButton>
      </div>
    </div>
  )
}

const EditTeacherSalaryModal: FC<EditTeacherSalaryModalProps> = (props) => {
  const { open, onClose } = props

  return (
    <Modal
      active={open}
      className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
      setActive={() => onClose()}
    >
      <EditTeacherSalaryModalContent {...props} />
    </Modal>
  )
}

export default EditTeacherSalaryModal
